import React, { useState, useEffect, useRef, useCallback } from 'react'
import {
  Modal,
  Input,
  Icon,
  message,
  Slider,
  Tooltip,
  Button,
  Progress,
  Spin
} from 'antd'
import { config } from '../../../../../constants'
import useAudioEditor from '../../../hooks/useAudioEditor'
import AudioSegmentEditModal from './AudioEditor/AudioSegmentEditModal'
// Modular Components (Moved to AudioEditor folder)
import {
  T,
  Label,
  Hr,
  Chip,
  speakerColor,
  speakerName,
  fmtSec
} from './AudioEditor/SharedLayout'
import DualTrackSection from './AudioEditor/DualTrackSection'
import TimelineTrack from './AudioEditor/TimelineTrack'
import TimelineSegmentRow from './AudioEditor/TimelineSegmentRow'
import PerShotAudioSection from './AudioEditor/PerShotAudioSection'

const { TextArea } = Input
const AUDIO_PREVIEW_BASE_URL = config.videoGenerationUrl + 'audio/'

/* ────────────────────────────────────────────────────────────────
   TimelineEditor
   Renders the proportional track and the vertical segment list.
──────────────────────────────────────────────────────────────── */
const TimelineEditor = ({ editor, characterMap, onEditSegment }) => {
  const { segments, currentScene, isPlaying, playheadMs } = editor
  const audioDuration = currentScene?.audio_duration
  const shots = currentScene?.shots || []

  if (!segments.length || !audioDuration) return null
  const totalMs = audioDuration * 1000

  return (
    <section>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 10
        }}
      >
        <Label>
          Segments
          <span
            style={{
              fontWeight: 400,
              color: T.inkLight,
              fontSize: 11,
              marginLeft: 4
            }}
          >
            {segments.length} · {fmtSec(audioDuration)}
          </span>
        </Label>
        <button
          onClick={() => editor.refreshTimeline()}
          disabled={editor.isLoading}
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            fontSize: 12,
            color: T.blue,
            display: 'flex',
            alignItems: 'center',
            gap: 4,
            padding: 0
          }}
        >
          <Icon
            type={editor.isLoading ? 'loading' : 'reload'}
            spin={editor.isLoading}
            style={{ fontSize: 11 }}
          />
          Refresh
        </button>
      </div>

      <TimelineTrack
        segments={segments}
        shots={shots}
        totalMs={totalMs}
        playheadMs={playheadMs}
        isPlaying={isPlaying}
        characterMap={characterMap}
        onSegmentClick={onEditSegment}
        onPauseResize={(idx, newMs) => editor.updatePause(idx, newMs)}
      />

      {/* Segment rows with highlighting */}
      <div
        style={{
          borderRadius: 8,
          border: `1px solid ${T.border}`,
          overflow: 'hidden'
        }}
      >
        {segments.map((seg, i) => (
          <TimelineSegmentRow
            key={i}
            segment={seg}
            index={i}
            isLast={i === segments.length - 1}
            playheadMs={playheadMs}
            isPlaying={isPlaying}
            isLoading={editor.isSegmentLoading(i)}
            pendingText={editor.getPendingText(i)}
            hasPending={editor.hasPendingEdit(i)}
            hasError={editor.hasError(i)}
            characterMap={characterMap}
            onEdit={onEditSegment}
          />
        ))}
      </div>
    </section>
  )
}

/* ────────────────────────────────────────────────────────────────
   VoiceoverModal – main export
──────────────────────────────────────────────────────────────── */
const VoiceoverModal = ({
  visible,
  scene,
  videoId,
  voices = [],
  loadingVoices,
  selectedVoice: globalSelectedVoice,
  narratorVoice,
  characterVoices = {},
  characterMap = {},
  token,
  onClose,
  onGenerate,
  onSceneStructureChange,
  onNavigateToStep,
  isClusteringAudio,
  audioClusteringProgress = 0,
  audioClusteringStatus = '',
  audioMode
}) => {
  const [voiceText, setVoiceText] = useState('')
  const [speedFactor, setSpeedFactor] = useState(0.7)
  const [exaggeration, setExaggeration] = useState(0.5)
  const [cfgWeight, setCfgWeight] = useState(0.8)
  const [isGenerating, setIsGenerating] = useState(false)
  const [showAdvanced, setShowAdvanced] = useState(false)
  const [previewPlaying, setPreviewPlaying] = useState(null)
  const [tab, setTab] = useState(
    audioMode === 'advanced' ? 'review' : 'generate'
  )
  const [editingSegmentIdx, setEditingSegmentIdx] = useState(null)
  const isEditModalOpen = editingSegmentIdx !== null
  const previewRef = useRef(null)

  const sceneKey = scene?.id

  // ── Editor hook ───────────────────────────────────────────────
  const editor = useAudioEditor({
    videoId,
    initialSceneStructure: scene ? { [sceneKey]: scene } : null,
    sceneKey,
    narratorVoice: narratorVoice || globalSelectedVoice,
    characterVoices,
    token,
    onSceneStructureChange: updated =>
      onSceneStructureChange &&
      onSceneStructureChange(
        updated && updated[sceneKey] !== undefined ? updated[sceneKey] : updated
      )
  })

  useEffect(() => {
    if (visible) {
      setTab(audioMode === 'advanced' ? 'review' : 'generate')
    }
  }, [visible, audioMode])

  // Sync scene structure whenever scene prop changes (e.g. after initial generation)
  useEffect(() => {
    if (scene && sceneKey) {
      editor.syncSceneStructure({ [sceneKey]: scene })
    }
  }, [scene, sceneKey])

  // Derived scene from editor (may differ after edits)
  const activeScene = editor.currentScene || scene

  const hasNar = Boolean(activeScene?.narration_audio_link)
  const hasDia = Boolean(activeScene?.dialogue_audio_link)
  const hasSimpleAudio = Boolean(activeScene?.audio_link)
  const hasAudio = hasNar || hasDia || hasSimpleAudio
  const isEstimated = Boolean(activeScene?.audio_duration_estimated)
  const charCount = voiceText?.length || 0
  const overLimit = charCount > 5000

  useEffect(() => {
    if (visible && scene && !voiceText) {
      const d = scene.aggregated_voiceover
      setVoiceText(typeof d === 'string' ? d : d?.text || '')
    }
  }, [visible])

  useEffect(
    () => () => {
      previewRef.current && previewRef.current.pause()
    },
    []
  )

  const handleGenerate = async () => {
    if (!globalSelectedVoice) {
      message.warning('Select a voice first')
      return
    }
    if (!(voiceText && voiceText.trim())) {
      message.warning('Enter voiceover text')
      return
    }
    setIsGenerating(true)
    try {
      await onGenerate({
        voice_id: globalSelectedVoice,
        text: voiceText,
        speed_factor: speedFactor,
        exaggeration,
        cfg_weight: cfgWeight
      })
      message.success('Voiceover generated')
      if (!isEasyAudioMode) {
        setTab('review')
      }
    } catch (e) {
      message.error(e?.message || 'Generation failed')
    } finally {
      setIsGenerating(false)
    }
  }

  const togglePreview = id => {
    if (previewPlaying === id) {
      previewRef.current && previewRef.current.pause()
      previewRef.current = null
      setPreviewPlaying(null)
    } else {
      previewRef.current && previewRef.current.pause()
      const a = new Audio(`${AUDIO_PREVIEW_BASE_URL}${id}`)
      a.play().catch(() => message.error('Preview failed'))
      a.onended = () => setPreviewPlaying(null)
      previewRef.current = a
      setPreviewPlaying(id)
    }
  }

  const voiceName = id => {
    if (!id) return null
    const v = voices.find(v => v?.id === id || v?.voice_id === id || v === id)
    return v?.name || v?.label || id
  }

  const isEasyAudioMode = audioMode === 'easy'

  /* clustering overlay */
  if (isClusteringAudio) {
    return (
      <Modal
        visible={visible}
        footer={null}
        width={440}
        maskClosable={false}
        onCancel={onClose}
        centered
        bodyStyle={{
          padding: '44px 36px',
          textAlign: 'center',
          fontFamily: T.font
        }}
      >
        <Spin size="large" />
        <div
          style={{ marginTop: 20, fontSize: 16, fontWeight: 600, color: T.ink }}
        >
          Generating Audio
        </div>
        <div
          style={{
            color: T.inkLight,
            marginTop: 4,
            marginBottom: 24,
            fontSize: 13
          }}
        >
          Rebuilding narration and dialogue tracks
        </div>
        <Progress
          percent={audioClusteringProgress}
          status={audioClusteringStatus === 'failed' ? 'exception' : 'active'}
          strokeColor={T.blue}
          style={{ maxWidth: 300, margin: '0 auto' }}
        />
        {audioClusteringStatus && (
          <div style={{ marginTop: 8, fontSize: 12, color: T.inkLight }}>
            {audioClusteringStatus}
          </div>
        )}
      </Modal>
    )
  }

  return (
    <Modal
      visible={visible}
      onCancel={isGenerating ? undefined : onClose}
      footer={null}
      width={700}
      maskClosable={!isGenerating}
      destroyOnClose
      centered
      bodyStyle={{ padding: 0, fontFamily: T.font }}
      style={{ fontFamily: T.font }}
    >
      {/* ── Header ───────────────────────────────────────────── */}
      <div
        style={{
          padding: '20px 28px 0',
          borderBottom: `1px solid ${T.border}`
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            paddingBottom: 14
          }}
        >
          <div>
            <div
              style={{
                fontSize: 16,
                fontWeight: 700,
                color: T.ink,
                letterSpacing: '-0.2px'
              }}
            >
              Voiceover Studio
            </div>
            <div style={{ fontSize: 12, color: T.inkLight, marginTop: 2 }}>
              {scene?.title || 'Scene'} · dual-track
            </div>
          </div>
          <div
            style={{
              display: 'flex',
              gap: 6,
              alignItems: 'center',
              paddingTop: 2
            }}
          >
            {hasAudio && (
              <Chip color={T.green} bg={T.greenLight} border={T.greenBorder}>
                ✓ Audio ready
              </Chip>
            )}
            {isEstimated && (
              <Chip color={T.amber} bg={T.amberLight} border={T.amberBorder}>
                Estimated
              </Chip>
            )}
          </div>
        </div>
        {/* tab bar */}
        <div style={{ display: 'flex' }}>
          {[
            ...(isEasyAudioMode
              ? [{ key: 'generate', icon: 'edit', label: 'Generate' }]
              : [
                  {
                    key: 'review',
                    icon: 'eye',
                    label: 'Review',
                    badge: hasAudio
                  }
                ])
          ].map(t => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              style={{
                padding: '10px 16px 9px',
                fontSize: 13,
                fontWeight: tab === t.key ? 600 : 400,
                color: tab === t.key ? T.ink : T.inkLight,
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                borderBottom:
                  tab === t.key
                    ? `2px solid ${T.ink}`
                    : '2px solid transparent',
                marginBottom: -1,
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                transition: 'color 0.12s, border-color 0.12s',
                fontFamily: T.font
              }}
            >
              <Icon type={t.icon} style={{ fontSize: 12 }} />
              {t.label}
              {t.badge && (
                <span
                  style={{
                    width: 5,
                    height: 5,
                    borderRadius: '50%',
                    background: T.green,
                    display: 'inline-block'
                  }}
                />
              )}
            </button>
          ))}
        </div>
      </div>

      {/* ── Body ─────────────────────────────────────────────── */}
      <div
        style={{
          padding: '24px 28px 8px',
          minHeight: 320,
          maxHeight: 540,
          overflowY: 'auto'
        }}
      >
        {/* ═══ GENERATE ════════════════════════════════════════ */}
        {tab === 'generate' && isEasyAudioMode && (
          <>
            {/* no voice */}
            {!globalSelectedVoice && (
              <div
                style={{
                  marginBottom: 20,
                  padding: '12px 16px',
                  background: T.redLight,
                  border: `1px solid ${T.redBorder}`,
                  borderRadius: 8,
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 10
                }}
              >
                <Icon
                  type="exclamation-circle"
                  style={{
                    color: T.red,
                    fontSize: 14,
                    marginTop: 2,
                    flexShrink: 0
                  }}
                />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: T.ink }}>
                    No voice selected
                  </div>
                  <div style={{ fontSize: 12, color: T.inkSub, marginTop: 2 }}>
                    Go to the Voiceover step to choose a voice.
                  </div>
                </div>
                <Button
                  size="small"
                  onClick={() => {
                    onClose()
                    onNavigateToStep && onNavigateToStep('voiceover')
                  }}
                  style={{ fontSize: 12, flexShrink: 0 }}
                >
                  Select voice
                </Button>
              </div>
            )}
            {/* voice chip */}
            {globalSelectedVoice && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  padding: '10px 14px',
                  marginBottom: 20,
                  background: T.blueLight,
                  border: `1px solid ${T.blueBorder}`,
                  borderRadius: 8
                }}
              >
                <Icon
                  type="sound"
                  style={{ color: T.blue, fontSize: 15, flexShrink: 0 }}
                />
                <div style={{ flex: 1 }}>
                  <div
                    style={{
                      fontSize: 10,
                      fontWeight: 700,
                      color: T.blue,
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}
                  >
                    Voice
                  </div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: T.ink }}>
                    {voiceName(globalSelectedVoice)}
                  </div>
                </div>
                <button
                  onClick={() => togglePreview(globalSelectedVoice)}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: 4,
                    color:
                      previewPlaying === globalSelectedVoice
                        ? T.blue
                        : T.inkLight,
                    fontSize: 18,
                    display: 'flex',
                    alignItems: 'center'
                  }}
                >
                  <Icon
                    type={
                      previewPlaying === globalSelectedVoice
                        ? 'pause-circle'
                        : 'play-circle'
                    }
                  />
                </button>
              </div>
            )}
            {/* script */}
            <div style={{ marginBottom: 20 }}>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'baseline',
                  marginBottom: 8
                }}
              >
                <Label>Script</Label>
                <span
                  style={{
                    fontSize: 11,
                    color: overLimit ? T.red : T.inkLight,
                    fontVariantNumeric: 'tabular-nums'
                  }}
                >
                  {charCount.toLocaleString()} / 5,000
                </span>
              </div>
              <TextArea
                rows={7}
                value={voiceText}
                onChange={e => setVoiceText(e.target.value)}
                placeholder="Enter the voiceover script…"
                disabled={isGenerating || !globalSelectedVoice}
                style={{
                  fontSize: 13,
                  lineHeight: 1.65,
                  borderColor: overLimit ? T.red : T.border,
                  borderRadius: 8,
                  resize: 'none',
                  fontFamily: T.font,
                  color: T.ink
                }}
              />
              {overLimit && (
                <div style={{ fontSize: 12, color: T.red, marginTop: 4 }}>
                  Exceeds 5,000 character limit
                </div>
              )}
            </div>
            {/* advanced */}
            <button
              onClick={() => {
                if (!isGenerating && globalSelectedVoice)
                  setShowAdvanced(p => !p)
              }}
              disabled={isGenerating || !globalSelectedVoice}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: 0,
                fontSize: 12,
                color: T.blue,
                fontWeight: 500,
                fontFamily: T.font,
                display: 'flex',
                alignItems: 'center',
                gap: 5,
                opacity: isGenerating || !globalSelectedVoice ? 0.4 : 1
              }}
            >
              <Icon
                type={showAdvanced ? 'up' : 'down'}
                style={{ fontSize: 10 }}
              />
              Advanced settings
            </button>
            {showAdvanced && (
              <div
                style={{
                  marginTop: 14,
                  padding: '16px 18px 4px',
                  background: T.surface,
                  borderRadius: 8,
                  border: `1px solid ${T.border}`
                }}
              >
                {[
                  {
                    key: 's',
                    label: 'Speed',
                    tip: '0.5× = half, 2.0× = double',
                    val: speedFactor,
                    set: setSpeedFactor,
                    min: 0.5,
                    max: 2.0,
                    step: 0.1,
                    marks: { 0.5: '0.5×', 1.0: '1×', 2.0: '2×' }
                  },
                  {
                    key: 'e',
                    label: 'Exaggeration',
                    tip: 'Expression intensity. Default 0.5',
                    val: exaggeration,
                    set: setExaggeration,
                    min: 0.1,
                    max: 2.0,
                    step: 0.1,
                    marks: { 0.1: '0.1', 1.0: '1.0', 2.0: '2.0' }
                  },
                  {
                    key: 'c',
                    label: 'CFG Weight',
                    tip: 'Script adherence. Default 0.8',
                    val: cfgWeight,
                    set: setCfgWeight,
                    min: 0.0,
                    max: 1.0,
                    step: 0.1,
                    marks: { 0.0: '0', 0.8: '0.8', 1.0: '1' }
                  }
                ].map(
                  ({ key, label, tip, val, set, min, max, step, marks }) => (
                    <div key={key} style={{ marginBottom: 22 }}>
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 6,
                          marginBottom: 8
                        }}
                      >
                        <span
                          style={{
                            fontSize: 12,
                            fontWeight: 600,
                            color: T.inkMid
                          }}
                        >
                          {label}
                        </span>
                        <Tooltip title={tip}>
                          <Icon
                            type="question-circle"
                            style={{
                              color: T.inkLight,
                              fontSize: 11,
                              cursor: 'help'
                            }}
                          />
                        </Tooltip>
                        <span
                          style={{
                            marginLeft: 'auto',
                            fontSize: 12,
                            fontWeight: 600,
                            color: T.ink,
                            fontVariantNumeric: 'tabular-nums'
                          }}
                        >
                          {val}
                        </span>
                      </div>
                      <Slider
                        value={val}
                        onChange={set}
                        min={min}
                        max={max}
                        step={step}
                        marks={marks}
                        disabled={isGenerating}
                        trackStyle={{ background: T.blue }}
                        handleStyle={{ borderColor: T.blue }}
                      />
                    </div>
                  )
                )}
              </div>
            )}
            {isGenerating && (
              <div
                style={{
                  marginTop: 16,
                  padding: '12px 16px',
                  borderRadius: 8,
                  background: T.blueLight,
                  border: `1px solid ${T.blueBorder}`,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  fontSize: 13,
                  color: T.blue,
                  fontWeight: 500
                }}
              >
                <Icon type="loading" spin style={{ fontSize: 14 }} />
                {isEasyAudioMode
                  ? 'Generating voiceover…'
                  : 'Generating dialogue tracks…'}
              </div>
            )}

            {/* Audio player for Easy Mode */}
            {isEasyAudioMode && hasSimpleAudio && !isGenerating && (
              <div style={{ marginTop: 24 }}>
                <Label style={{ display: 'block', marginBottom: 8 }}>
                  Generated Voiceover
                </Label>
                <audio
                  controls
                  src={activeScene.audio_link}
                  style={{ width: '100%', height: 40, outline: 'none' }}
                />
              </div>
            )}
          </>
        )}

        {/* ═══ REVIEW ══════════════════════════════════════════ */}
        {tab === 'review' && !isEasyAudioMode && (
          <>
            {hasAudio ? (
              <>
                <DualTrackSection
                  scene={activeScene}
                  isPlaying={editor.isPlaying}
                  onPlayingChange={editor.setIsPlaying}
                  onPlayheadChange={editor.setPlayheadMs}
                />
                <Hr />
                <TimelineEditor
                  editor={editor}
                  characterMap={characterMap}
                  onEditSegment={idx => setEditingSegmentIdx(idx)}
                />
                <Hr />
                <PerShotAudioSection shots={activeScene?.shots} />
              </>
            ) : (
              <div
                style={{
                  textAlign: 'center',
                  padding: '52px 0',
                  color: T.inkLight
                }}
              >
                <Icon
                  type="audio"
                  style={{
                    fontSize: 34,
                    display: 'block',
                    margin: '0 auto 10px',
                    color: T.borderLight
                  }}
                />
                <div style={{ fontSize: 14 }}>No audio generated yet</div>
                <button
                  onClick={() => setTab('generate')}
                  style={{
                    marginTop: 10,
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: 13,
                    color: T.blue,
                    fontFamily: T.font
                  }}
                >
                  Generate voiceover →
                </button>
              </div>
            )}
          </>
        )}

        {/* Audio Section Edit Modal */}
        <AudioSegmentEditModal
          visible={isEditModalOpen}
          onClose={() => setEditingSegmentIdx(null)}
          segment={editor.segments[editingSegmentIdx]}
          segmentIndex={editingSegmentIdx}
          editor={editor}
          characterMap={characterMap}
        />
      </div>

      {/* ── Footer ───────────────────────────────────────────── */}
      <div
        style={{
          padding: '14px 28px',
          borderTop: `1px solid ${T.border}`,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          background: T.surface
        }}
      >
        <div style={{ fontSize: 12, color: T.inkLight }}>
          {hasNar && hasDia
            ? '✓ Both tracks ready'
            : hasNar
            ? '✓ Narration ready · no dialogue'
            : hasSimpleAudio
            ? '✓ Audio ready'
            : ''}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <Button
            onClick={onClose}
            disabled={isGenerating}
            style={{ fontSize: 13 }}
          >
            Cancel
          </Button>
          {tab === 'generate' ? (
            <Button
              type="primary"
              onClick={handleGenerate}
              loading={isGenerating}
              disabled={
                !globalSelectedVoice ||
                !(voiceText && voiceText.trim()) ||
                overLimit
              }
              style={{
                fontSize: 13,
                fontWeight: 600,
                minWidth: 152,
                background: T.ink,
                borderColor: T.ink
              }}
            >
              {isGenerating ? 'Generating…' : 'Generate Voiceover'}
            </Button>
          ) : (
            isEasyAudioMode &&
            hasAudio && (
              <Button
                onClick={() => setTab('generate')}
                ghost
                style={{
                  fontSize: 13,
                  color: T.blue,
                  borderColor: T.blueBorder
                }}
              >
                <Icon type="reload" /> Regenerate
              </Button>
            )
          )}
        </div>
      </div>
    </Modal>
  )
}

export default VoiceoverModal
